import { alpha } from '@mui/material/styles';

/**
 * Helper functions for accessibility
 */
export function getAccessibilityUtils(theme) {
  return {
    /**
     * Calculate contrast ratio between two colors
     * @param {string} foreground - Foreground color (HEX)
     * @param {string} background - Background color (HEX)
     * @returns {number} - Contrast ratio (1-21)
     */
    getContrastRatio: (foreground, background) => {
      // Convert HEX to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
            }
          : null;
      };

      // Calculate luminance
      const luminance = (r, g, b) => {
        const a = [r, g, b].map((v) => {
          v /= 255;
          return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
      };

      const rgb1 = hexToRgb(foreground);
      const rgb2 = hexToRgb(background);
      const l1 = luminance(rgb1.r, rgb1.g, rgb1.b);
      const l2 = luminance(rgb2.r, rgb2.g, rgb2.b);

      // Return the contrast ratio (light:dark)
      return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    },

    /**
     * Check if contrast meets WCAG AA standards
     * @param {string} foreground - Foreground color (HEX)
     * @param {string} background - Background color (HEX)
     * @param {string} type - 'normal' or 'large' text
     * @returns {boolean} - Whether contrast meets WCAG AA
     */
    meetsWcagAA: (foreground, background, type = 'normal') => {
      const ratio = theme.a11y.getContrastRatio(foreground, background);
      return type === 'normal' ? ratio >= 4.5 : ratio >= 3;
    },

    /**
     * Ensure text color has sufficient contrast against background
     * @param {string} textColor - Original text color
     * @param {string} backgroundColor - Background color
     * @returns {string} - Adjusted text color if needed
     */
    ensureTextContrast: (textColor, backgroundColor) => {
      const ratio = theme.a11y.getContrastRatio(textColor, backgroundColor);

      if (ratio >= 4.5) return textColor;

      // If contrast is insufficient, return either black or white based on background
      return theme.a11y.getContrastRatio('#FFFFFF', backgroundColor) >
        theme.a11y.getContrastRatio('#000000', backgroundColor)
        ? '#FFFFFF'
        : '#000000';
    },
  };
}

/**
 * General theme utilities
 */
export function getThemeUtils(theme) {
  return {
    /**
     * Create responsive spacing based on breakpoints
     * @param {object} spacing - Object with spacing values for different breakpoints
     * @returns {object} - CSS-in-JS responsive spacing styles
     */
    responsiveSpacing: (spacing = {}) => {
      const { xs, sm, md, lg, xl } = spacing;
      const styles = {};

      if (xs !== undefined) styles.margin = theme.spacing(xs);
      if (sm !== undefined) styles[theme.breakpoints.up('sm')] = { margin: theme.spacing(sm) };
      if (md !== undefined) styles[theme.breakpoints.up('md')] = { margin: theme.spacing(md) };
      if (lg !== undefined) styles[theme.breakpoints.up('lg')] = { margin: theme.spacing(lg) };
      if (xl !== undefined) styles[theme.breakpoints.up('xl')] = { margin: theme.spacing(xl) };

      return styles;
    },

    /**
     * Create responsive font sizes
     * @param {object} sizes - Object with font sizes for different breakpoints
     * @returns {object} - CSS-in-JS responsive font size styles
     */
    responsiveFontSizes: (sizes = {}) => {
      const { xs, sm, md, lg, xl } = sizes;
      const styles = {};

      if (xs !== undefined) styles.fontSize = `${xs / 16}rem`;
      if (sm !== undefined) styles[theme.breakpoints.up('sm')] = { fontSize: `${sm / 16}rem` };
      if (md !== undefined) styles[theme.breakpoints.up('md')] = { fontSize: `${md / 16}rem` };
      if (lg !== undefined) styles[theme.breakpoints.up('lg')] = { fontSize: `${lg / 16}rem` };
      if (xl !== undefined) styles[theme.breakpoints.up('xl')] = { fontSize: `${xl / 16}rem` };

      return styles;
    },

    /**
     * Apply a color with appropriate contrast against background
     * @param {string} color - Main color to use
     * @param {string} backgroundColor - Background color
     * @param {number} opacity - Optional opacity for the color
     * @returns {string} - Color with appropriate contrast
     */
    getContrastColor: (color, backgroundColor, opacity = 1) => {
      if (opacity < 1) {
        color = alpha(color, opacity);
      }

      const hasGoodContrast = theme.a11y.meetsWcagAA(color, backgroundColor);
      if (hasGoodContrast) return color;

      // If contrast is poor, get a color with better contrast
      return theme.a11y.ensureTextContrast(color, backgroundColor);
    },

    /**
     * Get appropriate color for status display
     * @param {string} status - Status key from app.status
     * @param {string} variant - 'light', 'main', or 'dark'
     * @returns {string} - Appropriate color for the status
     */
    getStatusColor: (status, type, variant = 'main') => {
      const appColors = theme.palette.app;
      if (appColors[type]?.[status]?.[variant]) {
        return appColors[type][status][variant];
      }
      if (appColors[type]?.[status]) {
        return appColors[type][status];
      }
      // Default fallback
      return theme.palette.grey[500];
    },
  };
}

/**
 * Animation definitions
 */
export function getAnimations(theme) {
  return {
    transitions: {
      quick: `${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
      standard: `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
      complex: `${theme.transitions.duration.complex}ms ${theme.transitions.easing.easeInOut}`,
    },
    fadeIn: {
      animation: 'fadeIn 0.3s ease-in-out',
      keyframes: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `,
    },
    fadeInUp: {
      animation: 'fadeInUp 0.5s ease-out',
      keyframes: `
        @keyframes fadeInUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `,
    },
    fadeInDown: {
      animation: 'fadeInDown 0.5s ease-out',
      keyframes: `
        @keyframes fadeInDown {
          from { 
            opacity: 0;
            transform: translateY(-20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `,
    },
    slideIn: {
      animation: 'slideIn 0.4s ease-out',
      keyframes: `
        @keyframes slideIn {
          from { 
            transform: translateX(-30px);
            opacity: 0;
          }
          to { 
            transform: translateX(0);
            opacity: 1;
          }
        }
      `,
    },
    scaleIn: {
      animation: 'scaleIn 0.3s ease-out',
      keyframes: `
        @keyframes scaleIn {
          from { 
            transform: scale(0.9);
            opacity: 0;
          }
          to { 
            transform: scale(1);
            opacity: 1;
          }
        }
      `,
    },
    // Apply a keyframe animation
    applyAnimation: (animationName) => {
      const animation = theme.animations[animationName];
      if (!animation) return {};

      return {
        animation: animation.animation,
        '@global': {
          '@keyframes': {
            [animationName]: animation.keyframes,
          },
        },
      };
    },
  };
}
